const measurementId = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const analyticsEnabled = process.env.REACT_APP_GA_ENABLED === 'true';
const scriptMarkerAttribute = 'data-pp-ga-id';
const analyticsConsentStorageKey = 'pp_analytics_consent';

export const ANALYTICS_CONSENT_GRANTED = 'granted';
export const ANALYTICS_CONSENT_DENIED = 'denied';
export const ANALYTICS_CONSENT_UNSET = 'unset';

const deniedConsentPayload = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  functionality_storage: 'granted',
  security_storage: 'granted'
};

const grantedConsentPayload = {
  ...deniedConsentPayload,
  analytics_storage: 'granted'
};

let hasInitialized = false;
let hasConsentDefaults = false;

function canTrack() {
  return Boolean(analyticsEnabled && measurementId);
}

function getSafeStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function readStoredAnalyticsConsent() {
  const storage = getSafeStorage();
  const value = storage?.getItem(analyticsConsentStorageKey);

  if (value === ANALYTICS_CONSENT_GRANTED || value === ANALYTICS_CONSENT_DENIED) {
    return value;
  }

  return ANALYTICS_CONSENT_UNSET;
}

function persistAnalyticsConsent(value) {
  const storage = getSafeStorage();
  if (!storage) {
    return;
  }

  if (value === ANALYTICS_CONSENT_GRANTED || value === ANALYTICS_CONSENT_DENIED) {
    storage.setItem(analyticsConsentStorageKey, value);
    return;
  }

  storage.removeItem(analyticsConsentStorageKey);
}

function ensureBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function ensureConsentDefaults() {
  if (!canTrack() || !ensureBrowser() || hasConsentDefaults) {
    return;
  }

  const gtag = ensureGtag();
  gtag('consent', 'default', deniedConsentPayload);
  hasConsentDefaults = true;
}

function pushConsentUpdate(value) {
  if (!canTrack() || !ensureBrowser()) {
    return;
  }

  const gtag = ensureGtag();
  if (value === ANALYTICS_CONSENT_GRANTED) {
    gtag('consent', 'update', grantedConsentPayload);
    return;
  }

  gtag('consent', 'update', deniedConsentPayload);
}

function ensureDataLayer() {
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }

  return window.dataLayer;
}

function ensureGtag() {
  if (typeof window.gtag === 'function') {
    return window.gtag;
  }

  const dataLayer = ensureDataLayer();
  window.gtag = function gtag() {
    dataLayer.push(arguments);
  };

  return window.gtag;
}

function injectGtagScript() {
  const selector = `script[${scriptMarkerAttribute}="${measurementId}"]`;
  if (document.querySelector(selector)) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  script.setAttribute(scriptMarkerAttribute, measurementId);
  document.head.appendChild(script);
}

function getGtag() {
  if (!canTrack() || !ensureBrowser() || !hasInitialized) {
    return null;
  }

  return ensureGtag();
}

export function initAnalytics() {
  if (!canTrack() || hasInitialized || !ensureBrowser()) {
    return;
  }

  ensureConsentDefaults();
  injectGtagScript();

  const gtag = ensureGtag();
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false,
    page_location: getSafePageLocation()
  });

  hasInitialized = true;
}

export function getStoredAnalyticsConsent() {
  return readStoredAnalyticsConsent();
}

export function initializeAnalyticsConsent() {
  const storedConsent = readStoredAnalyticsConsent();
  if (!canTrack() || !ensureBrowser()) {
    return storedConsent;
  }

  ensureConsentDefaults();

  if (storedConsent === ANALYTICS_CONSENT_GRANTED) {
    pushConsentUpdate(ANALYTICS_CONSENT_GRANTED);
    initAnalytics();
    return storedConsent;
  }

  pushConsentUpdate(ANALYTICS_CONSENT_DENIED);
  return storedConsent;
}

export function grantAnalyticsConsent() {
  persistAnalyticsConsent(ANALYTICS_CONSENT_GRANTED);
  if (!canTrack() || !ensureBrowser()) {
    return;
  }

  ensureConsentDefaults();
  pushConsentUpdate(ANALYTICS_CONSENT_GRANTED);
  initAnalytics();
}

export function denyAnalyticsConsent() {
  persistAnalyticsConsent(ANALYTICS_CONSENT_DENIED);
  if (!canTrack() || !ensureBrowser()) {
    return;
  }

  ensureConsentDefaults();
  pushConsentUpdate(ANALYTICS_CONSENT_DENIED);
}

export function resetAnalyticsConsent() {
  persistAnalyticsConsent(ANALYTICS_CONSENT_UNSET);
  if (!canTrack() || !ensureBrowser()) {
    return;
  }

  ensureConsentDefaults();
  pushConsentUpdate(ANALYTICS_CONSENT_DENIED);
  hasInitialized = false;
}

function getSafePageLocation() {
  const location = new URL(window.location.href);
  location.searchParams.delete('session_id');
  return location.href;
}

export function trackPageView(pathname) {
  const gtag = getGtag();
  if (!gtag) {
    return;
  }

  const pagePath = pathname || `${window.location.pathname}${window.location.search}`;
  // A checkout session is a bearer reference: exclude it from analytics URLs.
  const safePath = pagePath.split('?')[0] === '/shop/success' ? '/shop/success' : pagePath;


  gtag('event', 'page_view', {
    page_path: safePath,
    page_location: getSafePageLocation(),
    page_title: document.title || undefined
  });
}

export function trackEvent(eventName, params = {}) {
  const gtag = getGtag();
  if (!gtag || !eventName) {
    return;
  }

  gtag('event', eventName, { ...params, page_location: getSafePageLocation() });
}

export function isAnalyticsConfigured() {
  return canTrack();
}
